/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useReducer, createContext, useContext } from "react";
import { Switch, useParams, Route, withRouter, Link, useHistory } from "react-router-dom";
import CreateIRModal from "../../components/Modals/CreateIRModal";
import fetchHelper from "../../services/fetchHelper";
import Information from "./information";
import Notes from "./Notes";
import Device from "./Device";
import Lead from "./Lead";
import AssociatedHospitals from "./AssociatedHospitals";
import RemoteMonitoring from "./RemoteMonitoring";
import HealthDemographics from "./HealthDemographics";
import Insurance from "./Insurance";
import Physician from "./Physician";
import History from "./History";
import SubLocations from "./SubLocations";
import InterrogationOrders from "./InterrogationOrders";
import savingspinner from "../../assets/esign-ui-icons/saving-ico-white.svg";
import UploadIRModal from "../../components/Modals/UploadIRModal";
import { featureCheck } from "../../utils/feature";
import sortBy from "lodash/sortBy";

import "./patients.css";

export const PatientsContext = createContext({});

const InformationActions = () => {
  const { information, setInformation } = useContext(PatientsContext);
  return (
    <div className="axnBtn" style={{ display: "inline-flex" }}>
      {information.openForm ? (
        <>
          <img id="loading-spinner-1" src={savingspinner} style={{ width: 0, height: 0, opacity: 0 }} />
          <button
            className="btn btnSuccess"
            onClick={(e) => {
              e.preventDefault();
              setInformation({ save: new Date().getTime() });
            }}
          >
            {information.loading ? (
              <img id="loading-spinner-2" src={savingspinner} style={{ width: 15 }} />
            ) : (
              <>
                <i className="ti ti-save" /> Save Form
              </>
            )}
          </button>
          <button
            className="btn btnUrgent"
            onClick={(e) => {
              e.preventDefault();
              setInformation({ openForm: false, cancel: new Date().getTime() });
            }}
          >
            Cancel
          </button>
        </>
      ) : (
        <button
          id="editPatientBtnId"
          className="btn btnTeal"
          onClick={(e) => {
            e.preventDefault();
            setInformation({ openForm: true });
          }}
        >
          <i className="ti ti-pencil"></i> Edit Patient
        </button>
      )}
    </div>
  );
};

const PatientNotesActions = () => {
  const {
    patientNotes,
    setPatientNotes,
    setSavePatientNote,
    patientTasks,
    setPatientTasks,
    setSavePatientTask,
  } = useContext(PatientsContext);
  const [patientEngagementTask, setPatientEngagementTask] = useState(false);

  useEffect(() => {
    enablePatientEngagementTask();
  }, []);

  /** To Enable or Disable Task Queues Tab */
  const enablePatientEngagementTask = () => {
    setPatientEngagementTask(featureCheck("patientEngagementFeature.enable"));
  };

  return (
    <div className="axnBtn" style={{ display: "inline-flex" }}>
      {!patientNotes.openForm ? (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setPatientNotes({ openForm: true });
            setPatientTasks({ openFormTask: false });
          }}
        >
          <button className="btn btnTeal">
            <i className="ti ti-plus" /> Create Note
          </button>
        </a>
      ) : (
        <>
          <a href="#">
            <button
              className="btn btnTeal"
              onClick={(e) => {
                e.preventDefault();
                setSavePatientNote(true);
              }}
            >
              <img id="loading-spinner-3" src={savingspinner} style={{ width: 0, height: 0, opacity: 0 }} />
              {patientNotes.saving ? (
                <img id="loading-spinner-4" src={savingspinner} style={{ width: 15 }} />
              ) : (
                "Save"
              )}
            </button>
          </a>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setPatientNotes({ openForm: false });
            }}
          >
            <button className="btn">Cancel</button>
          </a>
        </>
      )}
      {patientEngagementTask ? (
        !patientTasks.openFormTask ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setPatientTasks({ openFormTask: true });
              setPatientNotes({ openForm: false });
            }}
          >
            <button className="btn btnTeal">
              <i className="ti ti-plus" /> Task
            </button>
          </a>
        ) : (
          <>
            <a href="#">
              <button
                className="btn btnTeal"
                onClick={(e) => {
                  e.preventDefault();
                  setSavePatientTask(true);
                }}
              >
                <img id="loading-spinner-5" src={savingspinner} style={{ width: 0, height: 0, opacity: 0 }} />
                {patientTasks.saving ? (
                  <img id="loading-spinner-6" src={savingspinner} style={{ width: 15 }} />
                ) : (
                  "Save"
                )}
              </button>
            </a>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setPatientTasks({ openFormTask: false });
              }}
            >
              <button className="btn">Cancel</button>
            </a>
          </>
        )
      ) : null}
    </div>
  );
};

const DevicesActions = () => {
  const { devices, setDevices } = useContext(PatientsContext);
  return (
    <>
      {!devices.openForm ? (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setDevices({ openForm: true });
          }}
        >
          <button className="btn btnTeal" id="device-add">
            <i className="ti ti-plus" /> Add
          </button>
        </a>
      ) : (
        <>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setDevices({ addSave: new Date().getTime() });
            }}
          >
            <button className="btn btnTeal">
              {devices.saving ? (
                <img id="loading-spinner-7" src={savingspinner} style={{ width: 15 }} />
              ) : (
                "Save"
              )}
            </button>
          </a>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setDevices({ openForm: false });
            }}
          >
            <button className="btn">Cancel</button>
          </a>
        </>
      )}
    </>
  );
};

const LeadsActions = () => {
  const { leads, setLeads } = useContext(PatientsContext);
  return (
    <>
      {!leads.openForm ? (
        <button
          className="btn btnTeal"
          onClick={(e) => {
            e.preventDefault();
            setLeads({ openForm: true });
          }}
        >
          <i className="ti ti-plus" /> Add
        </button>
      ) : (
        <>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setLeads({ saved: new Date().getTime() });
            }}
          >
            <button className="btn btnTeal">
              {leads.saving ? (
                <img id="loading-spinner-8" src={savingspinner} style={{ width: 15 }} />
              ) : (
                "Save"
              )}
            </button>
          </a>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <button
              className="btn"
              onClick={(e) => {
                e.preventDefault();
                setLeads({ openForm: false });
              }}
            >
              Cancel
            </button>
          </a>
        </>
      )}
    </>
  );
};

const InsuranceActions = () => {
  const { insurance, setInsurance } = useContext(PatientsContext);
  return (
    <>
      {insurance.openForm ? (
        <>
          <button className="btn btnTeal" onClick={() => setInsurance({ saved: new Date().getTime() })}>
            {insurance.saving ? (
              <img id="loading-spinner-9" src={savingspinner} style={{ width: 15 }} />
            ) : (
              "Save"
            )}
          </button>
          <button className="btn" onClick={() => setInsurance({ openForm: false })}>
            Cancel
          </button>
        </>
      ) : (
        <button className="btn btnTeal" onClick={() => setInsurance({ openForm: true })}>
          Add
        </button>
      )}
    </>
  );
};

const PhysicianActions = () => {
  const { followingPhysicianMetadata, setFollowingPhysicianMetadata } = useContext(PatientsContext);
  const { patient } = useContext(PatientsContext);
  const cancel = () => {
    if (patient?.followingPhysician) {
      setFollowingPhysicianMetadata({
        selectedFollowingPhysician:
          followingPhysicianMetadata.oldFollowingPhysician || patient.followingPhysician,
        openForm: false,
      });
    } else {
      setFollowingPhysicianMetadata({
        selectedFollowingPhysician: "",
        openForm: false,
      });
    }
    if (patient?.referringPhysician) {
      setFollowingPhysicianMetadata({
        selectedReferringPhysician:
          followingPhysicianMetadata.oldReferringPhysician || patient.referringPhysician,
        openForm: false,
      });
    } else {
      setFollowingPhysicianMetadata({
        selectedReferringPhysician: "",
        openForm: false,
      });
    }
  };
  return (
    <>
      {!followingPhysicianMetadata.openForm ? null : (
        <>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setFollowingPhysicianMetadata({ saved: new Date().getTime() });
            }}
          >
            <button className="btn btnTeal">
              {followingPhysicianMetadata.saving ? (
                <img id="loading-spinner-10" src={savingspinner} style={{ width: 15 }} />
              ) : null}{" "}
              Save
            </button>
          </a>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <button
              className="btn"
              onClick={(e) => {
                e.preventDefault();
                cancel();
              }}
            >
              Cancel
            </button>
          </a>
        </>
      )}
    </>
  );
};

function leadsReducer(state, action) {
  switch (action.type) {
    case "add":
      return { ...state, list: [action.lead, ...state.list], openForm: false, saving: false, saved: null };
    case "update":
      return {
        ...state,
        list: [
          ...state.list.filter((i) => {
            if (i.id == action.lead.id) return action.lead;
            return i;
          }),
        ],
        openForm: false,
        saving: false,
        saved: null,
      };
    case "remove":
      return { ...state, list: [...state.list.filter((i) => i.id != action.lead.id)] };
  }

  return { saved: null, ...state, ...action };
}

function followingPhysicianReducer(state, action) {
  return { ...state, ...action };
}

function hospitalsReducer(state, action) {
  return { ...state, ...action };
}

function rMonitoringReducer(state, action) {
  return { ...state, ...action };
}

function insuranceReducer(state, action) {
  switch (action.type) {
    case "remove-list":
      return {
        list: state.list.filter((i) => i.id != action.data.id),
      };
    case "add-list":
      return {
        saved: null,
        saving: false,
        list: [action.data, ...state.list],
      };
    case "update-list":
      return {
        ...state,
        saving: null,
        saved: null,
        list: [
          ...state.list.map((i) => {
            if (i.id === action.data.id) return action.data;
            return i;
          }),
        ],
      };
  }
  return { ...state, saving: null, saved: null, ...action };
}

function patientReducer(state, action) {
  return { ...state, ...action };
}

const Patients = ({ match, location }) => {
  let history = useHistory();

  const { pathname } = location;
  const { path, url } = match;
  const [patient, setPatient] = useState(null);
  const [information, setInformation] = useState({});
  const [patientTasksOffset, setPatientTasksOffset] = useState(0);
  const [patientNotes, setPatientNotes] = useState({ loading: false, list: [] });
  const [patientTasks, setPatientTasks] = useState({ loading: false, list: [] });
  const [savePatientNote, setSavePatientNote] = useState(false);
  const [savePatientTask, setSavePatientTask] = useState(false);
  const [devices, setDevices] = useState({ loading: false, list: [], saving: false });
  const [reasons, setReasons] = useState();
  const [leads, setLeads] = useReducer(leadsReducer, { openForm: false, list: [] });
  const [followingPhysicianMetadata, setFollowingPhysicianMetadata] = useReducer(followingPhysicianReducer, {
    openForm: false,
    availablePhysicians: [],
  });
  const [hospital, setHospital] = useReducer(hospitalsReducer, {});
  const [rMonitoring, setRMonitoring] = useReducer(rMonitoringReducer, {});
  const [insurance, setInsurance] = useReducer(insuranceReducer, {});
  const [patientData, setPatientData] = useReducer(patientReducer, {});
  const [lastSyncUpdate, setLastSyncUpdate] = useState(null);

  const { i } = useParams();

  useEffect(() => {
    if (patient?.followingPhysician) {
      setFollowingPhysicianMetadata({ selectedFollowingPhysician: patient.followingPhysician });
    }
    if (patient?.referringPhysician) {
      setFollowingPhysicianMetadata({ selectedReferringPhysician: patient.referringPhysician });
    }
  }, [patient]);

  useEffect(() => {
    fetchPatient();
  }, []);

  const fetchPatient = async () => {
    try {
      const data = await fetchHelper.get({
        url: "/patientmanagement/rest/patients/" + i + "/",
      });
      setPatient(data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPatientNotes = async () => {
    setPatientNotes({ ...patientNotes, loading: true });
    try {
      const data = await fetchHelper.get({
        url: "/patientmanagement/rest/patientnote/",
        urlParams: {
          where: '{"patient.id":{"$eq":"' + i + '"}}',
          sort: "-sticky,-noteDate",
        },
      });
      setPatientNotes({ ...patientNotes, loading: false, list: data });
    } catch (err) {
      setPatientNotes({ ...patientNotes, loading: false });
      console.log(err);
    }
  };

  const fetchDeviceInfo = async () => {
    try {
      updateDevices({ loading: true, list: [] });
      const data = await fetchHelper.get({
        url: "/patientmanagement/rest/devices/findByPatientId/" + i,
      });
      updateDevices({ list: data, loading: false });
    } catch (err) {
      updateDevices({ loading: false });
      console.log(err);
    }
  };

  const fetchReasons = async () => {
    try {
      const data = await fetchHelper.get({
        url: `/patientmanagement/rest/engagement/reasons`,
      });
      setReasons(sortBy(data, ["description"]));
    } catch ({ errors }) {
      console.log(errors);
    }
  };

  const updatePatientNotes = (data) => setPatientNotes({ ...patientNotes, ...data });
  const updatePatientTasks = (data) => setPatientTasks({ ...patientTasks, ...data });
  const updateInformation = (data) => setInformation({ ...information, ...data });
  const updateDevices = (data) => setDevices({ ...devices, ...data });
  const updateReasons = (data) => setReasons({ ...reasons, ...data });

  const returnToMaster = () => {
    history.push(localStorage.getItem("master-location"));
  };

  if (!patient) return null;
  return (
    <PatientsContext.Provider
      value={{
        patient,
        fetchPatientNotes,
        patientNotes,
        patientTasks,
        patientTasksOffset,
        setPatientTasksOffset,
        setPatientTasks: updatePatientTasks,
        setPatientNotes: updatePatientNotes,
        information,
        setInformation: updateInformation,
        fetchDeviceInfo,
        devices,
        setDevices: updateDevices,
        fetchReasons,
        reasons,
        setReasons: updateReasons,
        leads,
        setLeads,
        addPatientNotes: (data) => {
          const list = [...patientNotes.list, data];
          setPatientNotes({ ...patientNotes, list, openForm: false });
        },
        addPatientTask: (data) => {
          const list = data;
          setPatientTasks({ ...patientTasks, list, openFormTask: false });
        },
        savePatientNote,
        setSavePatientNote,
        savePatientTask,
        setSavePatientTask,
        followingPhysicianMetadata,
        setFollowingPhysicianMetadata,
        hospital,
        setHospital,
        rMonitoring,
        setRMonitoring,
        insurance,
        setInsurance,
        patientData,
        setPatientData,
        setPatient: (data) => setPatient(data),
        fetchPatient,
        lastSyncUpdate,
        setLastSyncUpdate,
      }}
    >
      <div id="patient-profile" className="containerFlex maxHd">
        <div className="contentArea">
          <div className="axnArea mb0">
            <div className="row rowStretch mb0">
              <div className="col col100 alignLeft selectsm">
                <div className="selectsm">
                  <select className="m0">
                    <option>Patient Information</option>
                    <option>Patient Notes</option>
                    <option>Device Information</option>
                    <option>Lead Information</option>
                    <option>Associated Hospitals</option>
                    <option>Remote Monitoring</option>
                    <option>Health Demographics</option>
                    <option>Following Physician</option>
                    <option>Insurance Information</option>
                    <option>History</option>
                    <option>Interrogation Orders</option>
                    <option>Sub Locations</option>
                  </select>
                </div>
              </div>
              <div className="axnArea mb0">
                <div className="row rowStretch mb0">
                  <div className="col col-s-100 col40 alignLeft title-back">
                    <h3>
                      Patient:{" "}
                      <span>
                        {patient.name} {patient.family}
                      </span>
                    </h3>
                    <a
                      onClick={() => {
                        returnToMaster();
                      }}
                      className="back-link"
                    >
                      &#60;BACK
                    </a>
                  </div>
                  <div
                    className="col col-s-100 col60 alignRight btn-group"
                    style={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <div className="axnBtn">
                      <a href="#popupIR">
                        <button className="btn btnTeal" id="create-ir">
                          <i className="ti ti-plus" /> Create IR
                        </button>
                      </a>
                    </div>
                    <div className="axnBtn">
                      <a href="#uploadIR" id="upload-report-btn">
                        <button className="btn btnTeal" id="upload-ir">
                          <i className="ti ti-plus" /> Upload IR
                        </button>
                      </a>
                    </div>
                    <Route exact path={path}>
                      <InformationActions />
                    </Route>
                    <Route path={url + "/notes"}>
                      <PatientNotesActions />
                    </Route>
                    <Route path={url + "/devices"}>
                      <DevicesActions />
                    </Route>
                    <Route path={url + "/leads"}>
                      <LeadsActions />
                    </Route>
                    <Route path={url + "/physician"}>
                      <PhysicianActions />
                    </Route>
                    <Route path={url + "/insurance"}>
                      <InsuranceActions />
                    </Route>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col20 side-nav showBs">
              <nav className="tabs tabY">
                <Link className={pathname === url ? "tabActive" : null} to={url}>
                  <span className="tab-text">Patient Information</span>
                </Link>
                <Link className={pathname === url + "/notes" ? "tabActive" : null} to={url + "/notes"}>
                  <span className="tab-text">Patient Notes</span>
                </Link>
                <Link className={pathname === url + "/devices" ? "tabActive" : null} to={url + "/devices"}>
                  <span className="tab-text">Device Information</span>
                </Link>
                <Link className={pathname === url + "/leads" ? "tabActive" : null} to={url + "/leads"}>
                  <span className="tab-text">Lead Information</span>
                </Link>
                <Link
                  className={pathname === url + "/hospitals" ? "tabActive" : null}
                  to={url + "/hospitals"}
                >
                  <span className="tab-text">Associated Hospitals</span>
                </Link>
                <Link
                  className={pathname === url + "/remote-monitoring" ? "tabActive" : null}
                  to={url + "/remote-monitoring"}
                >
                  <span className="tab-text">Remote Monitoring</span>
                </Link>
                <Link
                  className={pathname === url + "/physician" ? "tabActive" : null}
                  to={url + "/physician"}
                >
                  <span className="tab-text">Following Physician</span>
                </Link>
                <Link
                  className={pathname === url + "/insurance" ? "tabActive" : null}
                  to={url + "/insurance"}
                >
                  <span className="tab-text">Insurance Information</span>
                </Link>
                <Link className={pathname === url + "/history" ? "tabActive" : null} to={url + "/history"}>
                  <span className="tab-text">History</span>
                </Link>
                {patient?.patientLocation[0]?.location?.orm ? (
                  <Link
                    className={pathname === `${url}/interrogationorders` ? "tabActive" : null}
                    to={`${url}/interrogationorders`}
                  >
                    <span className="tab-text">Interrogation Orders</span>
                  </Link>
                ) : null}

                <Link
                  className={pathname === url + "/sublocations" ? "tabActive" : null}
                  to={url + "/sublocations"}
                >
                  <span className="tab-text">Sub Locations</span>
                </Link>
              </nav>
            </div>
            <div className="col col-s-100 col80 force-max">
              <Switch>
                <Route exact path={path}>
                  <Information />
                </Route>
                <Route path={path + "/notes"}>
                  <Notes />
                </Route>
                <Route path={path + "/devices"}>
                  <Device />
                </Route>
                <Route path={path + "/leads"}>
                  <Lead />
                </Route>
                <Route path={path + "/hospitals"}>
                  <AssociatedHospitals />
                </Route>
                <Route path={path + "/remote-monitoring"}>
                  <RemoteMonitoring />
                </Route>
                <Route path={path + "/health-demographics"}>
                  <HealthDemographics />
                </Route>
                <Route path={path + "/physician"}>
                  <Physician />
                </Route>
                <Route path={path + "/insurance"}>
                  <Insurance />
                </Route>
                <Route path={path + "/history"}>
                  <History />
                </Route>
                <Route path={path + "/interrogationorders"}>
                  <InterrogationOrders />
                </Route>
                <Route path={path + "/sublocations"}>
                  <SubLocations />
                </Route>
              </Switch>
            </div>
          </div>
        </div>

        {/* Create Report Modal */}
        <CreateIRModal patient={patient} />
        {/* Create Report Modal */}

        {/* Upload Report Modal */}
        <UploadIRModal patientId={i} />
        {/* Upload Report Modal */}

        {/* Added serve as backdrop start*/}
        <a href="#" className="close-popup" />
        {/* Added serve as backdrop end*/}
        {/* .modal */}
      </div>
    </PatientsContext.Provider>
  );
};

export default withRouter(Patients);
